import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useQuery } from '@apollo/client';

import { ExchangeModeEnum } from '@libs/backend-common';
import { Button, Typography } from '@libs/components';
import {
  formatBalance,
  getPercentageWithSymbol,
  formatAmountWithSymbol,
} from '@libs/helpers';
import { CurrencySymbol, CurrencyType } from '@libs/types';

import {
  GetExchangeFeesWithRangesData,
  getExchangeFeesWithRangesQuery,
} from 'src/gql';
import {
  useApiResponse,
  useBalanceVisibility,
  useTranslation,
} from 'src/hooks';
import { DashboardConvertFeeCardLoader } from 'src/content-loaders';
import { DesertedIcon } from 'src/assets';
import { DataWithLoader } from 'src/components';

import Bullets from '../bullets/Bullets';

import {
  StatWrapper,
  StatusBar,
  Container,
  CircleCenter,
  Circle,
  DynamicBar,
  Tooltip,
  Body,
  RangeWrapper,
  Title,
  Footer,
} from './styles';

const MAX_WIDTH = 100;

type ConverFeeRange = {
  className?: string;
};

const ConvertFeeRange: React.FC<ConverFeeRange> = ({ className }) => {
  const { colors } = useTheme();
  // const { currency } = useCurrency();
  const currency = CurrencyType.USD;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleMoreButton = () => {
    navigate('/loyalty');
  };
  const { isBalanceHidden } = useBalanceVisibility();

  const getExchangeFeesWithRangesResp = useQuery(
    getExchangeFeesWithRangesQuery,
    {
      variables: {
        record: { valueAssetCode: currency, mode: ExchangeModeEnum.CONVERT },
      },
      fetchPolicy: 'network-only',
    }
  );

  const { response: getExchangeFeesWithRangesResponse, loading } =
    useApiResponse(getExchangeFeesWithRangesResp);
  const userVolume =
    Number(
      getExchangeFeesWithRangesResponse?.getExchangeFeesWithRanges.userVolume
    ) || 0;

  const calculateToAmount = (
    nextItem: GetExchangeFeesWithRangesData,
    currencySign: string
  ) => {
    if (!nextItem) return ' +';
    return `-${formatBalance(nextItem.greaterThan, false, 0)}${currencySign}`;
  };

  const isActiveRange = (
    currentItem: GetExchangeFeesWithRangesData,
    nextItem: GetExchangeFeesWithRangesData,
    userVolume: number
  ) => {
    const upperLimit = nextItem?.greaterThan ?? userVolume + 1;
    const lowerLimit = currentItem.greaterThan;
    return Number(userVolume) <= upperLimit && Number(userVolume) >= lowerLimit;
  };

  const calculateBarWidth = (
    currentItem: GetExchangeFeesWithRangesData,
    nextItem: GetExchangeFeesWithRangesData,
    userVolume: number,
    index: number,
    isActive: boolean
  ) => {
    if (!isActive) return 0;
    // setFee(currentItem.buyRate.toString());

    // Special case for index 2
    if (index === 2) return MAX_WIDTH;

    const difference = nextItem?.greaterThan
      ? nextItem.greaterThan - currentItem.greaterThan
      : currentItem.greaterThan;

    return (MAX_WIDTH * Number(userVolume)) / difference;
  };

  const getTextColor = (isActive: boolean) => {
    return isActive ? colors.interactive.brandColor : colors.text.secondary;
  };
  const getPercentColor = (isActive: boolean) => {
    return isActive ? colors.interactive.brandColor : colors.text.default;
  };
  const feeRanges =
    getExchangeFeesWithRangesResponse?.getExchangeFeesWithRanges.data || [];

  const activeFee =
    (getExchangeFeesWithRangesResponse?.getExchangeFeesWithRanges.currentFee
      ?.buyRate || 0) * 100;

  return (
    <Container className={className}>
      <Body>
        <Title type="bodyButton" fontFamily="primaryMedium" className="title">
          {t('common_convert_fee_title')}
        </Title>
        <DataWithLoader
          hasData={!!feeRanges.length}
          loader={<DashboardConvertFeeCardLoader />}
          loading={loading}
          icon={DesertedIcon}
          emptyStateTitle={t('common_not_found')}
        >
          <>
            {isBalanceHidden ? (
              <Bullets bulletAmount={3} />
            ) : (
              <Typography
                type="largeTitle"
                fontFamily="primaryBold"
                className="activeFee"
              >
                {activeFee}%
              </Typography>
            )}
            <RangeWrapper>
              {feeRanges.map((currentItem, index, array) => {
                const nextItem = array[index + 1];
                const currencySign = CurrencySymbol[currency];
                const toAmount = calculateToAmount(nextItem, currencySign);
                const isActive = isActiveRange(
                  currentItem,
                  nextItem,
                  userVolume
                );
                const calculatedActiveBarWidth = calculateBarWidth(
                  currentItem,
                  nextItem,
                  userVolume,
                  index,
                  isActive
                );
                const textColor = getTextColor(
                  isBalanceHidden ? false : isActive
                );
                const textPercetColor = getPercentColor(
                  isBalanceHidden ? false : isActive
                );
                const barWidth =
                  currentItem.greaterThan < userVolume && !isActive
                    ? MAX_WIDTH
                    : calculatedActiveBarWidth;

                return (
                  <StatWrapper key={index}>
                    <StatusBar>
                      <DynamicBar
                        style={{ width: `${isBalanceHidden ? 0 : barWidth}%` }}
                      >
                        {isActive && !isBalanceHidden && (
                          <Circle>
                            <CircleCenter>
                              {!!userVolume && (
                                <Tooltip isEnd={index === 2}>
                                  <Typography
                                    type="caption"
                                    fontFamily="primaryMedium"
                                    color={colors.surface.default}
                                  >
                                    {formatAmountWithSymbol({
                                      amount: userVolume,
                                      symbol: currencySign,
                                    })}
                                  </Typography>
                                </Tooltip>
                              )}
                            </CircleCenter>
                          </Circle>
                        )}
                      </DynamicBar>
                    </StatusBar>
                    <Typography
                      type="bodyButton"
                      fontFamily="primaryBold"
                      color={textPercetColor}
                    >
                      {getPercentageWithSymbol(currentItem.buyRate)}
                    </Typography>
                    <Typography
                      type="bodyButton"
                      fontFamily="primaryRegular"
                      color={textColor}
                    >
                      {formatBalance(currentItem.greaterThan, false, 0)}
                      {currencySign}
                      {toAmount}
                    </Typography>
                  </StatWrapper>
                );
              })}
            </RangeWrapper>
          </>
        </DataWithLoader>
      </Body>
      <Footer className="footer">
        <Button
          size="sm"
          buttonType="secondary"
          title={t('common_details')}
          onClick={handleMoreButton}
        />
      </Footer>
    </Container>
  );
};

export default ConvertFeeRange;
